import { useCallback, useState } from "react";
import { Buton, Cip, Modal, etiket, girdi } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { dosyaSec } from "@/lib/veri";

/**
 * D-034: rapor PDF'i yüklenen her yerde aynı "Partial" + tek sayfa alanı. Partial işaretsizse PDF'in tamamı belgedir;
 * işaretliyse bir sayfa ("9") ya da aralık ("1-4") zorunludur. Sunucu biçimi ve PDF sayfa sayısını ayrıca denetler.
 */
export type SayfaSecimi = { kismi: boolean; sayfalar: string };
export const BOS_SAYFA: SayfaSecimi = { kismi: false, sayfalar: "" };
export type SayfaAraligi = { sayfaBas: number; sayfaSon: number };

const BICIM = /^(\d+)\s*(?:[-–—]\s*(\d+))?$/;

/** Partial işaretsizse her zaman geçerli; işaretliyse "9" ya da "1-4" ve ilk ≤ son. */
export function sayfaGecerli(s: SayfaSecimi) {
  if (!s.kismi) return true;
  const m = BICIM.exec(s.sayfalar.trim());
  return Boolean(m) && Number(m![1]) >= 1 && Number(m![1]) <= Number(m![2] ?? m![1]);
}

/** dosyaYukle'ye eklenecek form alanları. */
export const sayfaAlanlari = (s: SayfaSecimi): Record<string, string> => (s.kismi ? { kismi: "true", sayfalar: s.sayfalar.trim() } : {});

/** "p. 3–5" / "p. 9"; aralık yoksa boş (PDF'in tamamı). */
export const sayfaEtiketi = (a: SayfaAraligi | null | undefined) => (a ? (a.sayfaBas === a.sayfaSon ? `p. ${a.sayfaBas}` : `p. ${a.sayfaBas}–${a.sayfaSon}`) : "");

/**
 * Kalemin "p.N" / "rw s.N" referansı belgenin kendi sayfasıdır. Belge birleştirilmiş bir PDF'in parçasıysa (D-034 "Partial")
 * PDF'teki fiziksel sayfa aralığın başına göre kayar. Triyaj çipleri ve kesim planı pencereleri aynı hesabı kullanır (U-037).
 */
export const pdfSayfasi = (sayfa: number, a?: SayfaAraligi | null) => sayfa + (a ? a.sayfaBas - 1 : 0);

/** Belge bağlantısı; aralık varsa PDF o sayfadan açılır. */
export const belgeLinki = (dokumanId: string, a?: SayfaAraligi | null) => `/api/dokuman/${dokumanId}${a ? `#page=${a.sayfaBas}` : ""}`;

/** Var olan bir yükleme penceresine gömülen alan: "Partial" kutusu ve işaretliyse sayfa kutusu. */
export function SayfaAlani({ deger, onChange, disabled }: { deger: SayfaSecimi; onChange: (s: SayfaSecimi) => void; disabled?: boolean }) {
  const hatali = deger.kismi && deger.sayfalar.trim() !== "" && !sayfaGecerli(deger);
  return (
    <div className="mt-3">
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={disabled} checked={deger.kismi} onChange={(e) => onChange({ ...deger, kismi: e.target.checked })} /> <b>Partial</b> — {cevir("only some pages of this PDF are this document")}</label>
      {deger.kismi && (
        <div className="mt-2">
          <label className={etiket}>{cevir("Pages of this PDF (mandatory when Partial)")}</label>
          <input className={girdi + " w-40" + (hatali ? " border-bad!" : "")} disabled={disabled} value={deger.sayfalar} placeholder={cevir("e.g. 9 or 1-4")} onChange={(e) => onChange({ ...deger, sayfalar: e.target.value })} />
          <p className="mt-1 text-xs text-ink-3">{cevir("One page (e.g. 9) or a range (e.g. 1-4). Must be within the PDF.")}</p>
        </div>
      )}
    </div>
  );
}

export type PdfSecim = { dosya: File; sayfa: SayfaSecimi };

/**
 * Doğrudan dosya seçilen yükleme noktaları için: `const { pdfSec, pencere } = usePdfSec();` — ekran `{pencere}`'yi bir
 * kez çizer; `await pdfSec(başlık)` PDF + Partial + sayfa penceresini açar, iptalde null döner.
 */
export function usePdfSec() {
  const [istek, setIstek] = useState<{ baslik: string; accept: string; coz: (s: PdfSecim | null) => void } | null>(null);
  const pdfSec = useCallback((baslik: string, accept = ".pdf,application/pdf") => new Promise<PdfSecim | null>((coz) => setIstek({ baslik, accept, coz })), []);
  const pencere = istek ? <PdfSecPenceresi baslik={istek.baslik} accept={istek.accept} bitir={(s) => { istek.coz(s); setIstek(null); }} /> : null;
  return { pdfSec, pencere };
}

function PdfSecPenceresi({ baslik, accept, bitir }: { baslik: string; accept: string; bitir: (s: PdfSecim | null) => void }) {
  const [dosya, setDosya] = useState<File | null>(null);
  const [sayfa, setSayfa] = useState<SayfaSecimi>(BOS_SAYFA);
  const pdfMi = !dosya || /\.pdf$/i.test(dosya.name) || dosya.type === "application/pdf";
  return (
    <Modal baslik={baslik} onKapat={() => bitir(null)}>
      <div className="flex flex-wrap items-center gap-2">
        <Buton kucuk onClick={async () => { const f = await dosyaSec(accept); if (f) setDosya(f); }}>{dosya ? cevir("Change file") : cevir("Select PDF")}</Buton>
        {dosya ? <Cip>📄 {dosya.name}</Cip> : <span className="text-sm text-ink-3">{cevir("not selected")}</span>}
      </div>
      {pdfMi && <SayfaAlani deger={sayfa} onChange={setSayfa} />}
      <div className="mt-4 flex gap-2">
        <Buton tur="birincil" disabled={!dosya || (pdfMi && !sayfaGecerli(sayfa))} onClick={() => { if (dosya) bitir({ dosya, sayfa: pdfMi ? sayfa : BOS_SAYFA }); }}>{cevir("Upload")}</Buton>
        <Buton onClick={() => bitir(null)}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}
